import { useState } from 'react';
import StaffTable, { type StaffRecord } from '@univdiam/ui/customComponents/Staff/StaffTable';
import { AddStaffFormSchema } from '@univdiam/ui/customComponents/forms/AddStaff';
import TicketIcon from '@/assets/FeaturedIcon.svg';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import api from '@/http/api';
import { toast } from 'sonner';
import { useAuthStore } from '@/store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDeactivateStaff } from '@/sharedData/Staff';

const staffCreation = (data: z.infer<typeof AddStaffFormSchema>) => {
    const { staffName, emailID, phoneNumber, phoneNumberCode, jobTitle, profilePic } = data;
    return api.post(`/staff`, {
        staffName, emailID, jobTitle, profilePic,
        phoneNumber: `${phoneNumberCode}${phoneNumber}`
    });
};

const staffUpdation = (data: z.infer<typeof AddStaffFormSchema>, staffId: string) => {
    const { staffName, emailID, phoneNumber, phoneNumberCode, jobTitle, profilePic } = data;
    return api.patch(`/staff`, {
        staffName, emailID, jobTitle, profilePic, staffId,
        phoneNumber: `${phoneNumberCode}${phoneNumber}`
    });
};

const getAllStaff = () => api.get(`/staff`);

export default function StaffManagement() {
    const { user } = useAuthStore();

    // Only the tenant owner (CUSTOMER) manages staff — a STAFF account gets a read-only list.
    const canManage = user?.role === 'CUSTOMER';

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [selectedStaff, setSelectedStaff] = useState<StaffRecord | null>(null);
    const [addStaffEvent, setAddStaffEvent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const staffIdFromQuery = searchParams.get('staffId');

    const { mutate: deactivateStaff } = useDeactivateStaff();

    const handleAddStaffOpenChange = (open: boolean) => {
        setAddStaffEvent(open);
        if (!open) {
            const params = new URLSearchParams(window.location.search);
            params.delete('staffId');
            navigate({ search: params.toString() }, { replace: true });
        }
    };

    const handleSelectStaff = (staff: StaffRecord | null) => {
        setSelectedStaff(staff);
        if (staff) {
            const params = new URLSearchParams(window.location.search);
            params.set('staffId', staff.id);
            navigate({ search: params.toString() }, { replace: true });
        }
    };

    const { mutate, isError, isPending, error } = useMutation({
        mutationFn: (values: z.infer<typeof AddStaffFormSchema>) => {
            if (selectedStaff && staffIdFromQuery) {
                return staffUpdation(values, staffIdFromQuery);
            }
            return staffCreation(values);
        },
        onSuccess: async () => {
            toast.success('Staff added successfully!', { position: 'top-center' });
            handleAddStaffOpenChange(false);
            setSelectedStaff(null);
            queryClient.invalidateQueries({ queryKey: ['allStaff'] });
        }
    });

    const handleSendToUpload = async (files: File[]) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));

            const response = await api.post(
                `/upload?customerId=${user?.customer_id}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            return response.data.files ?? [];
        } catch (error) {
            toast.error(`Error occured while uploading media : ${error}`);
        } finally {
            setIsLoading(false);
        }
    };

    const { data, isPending: isStaffLoading } = useQuery({
        queryKey: ['allStaff'],
        queryFn: () => getAllStaff(),
    });

    const staffs = data?.data?.allStaff as StaffRecord[];

    return (
        <StaffTable
            icon={TicketIcon}
            staffs={staffs}
            isLoading={isStaffLoading}
            canManage={canManage}
            onView={(staffId) => navigate(`/manage-staff/${staffId}`)}
            onDeactivate={(staffId) => deactivateStaff({ staffId, status: 0 }, {
                onSuccess: () => toast.success('Staff deactivated!'),
            })}
            onActivate={(staffId) => deactivateStaff({ staffId, status: 1 }, {
                onSuccess: () => toast.success('Staff activated!'),
            })}
            addStaffOpen={addStaffEvent}
            onAddStaffOpenChange={handleAddStaffOpenChange}
            selectedStaff={selectedStaff}
            onSelectStaff={handleSelectStaff}
            onCreateStaff={(values) => mutate(values)}
            handleSendToUpload={handleSendToUpload}
            mediaLoading={isLoading}
            isSubmitError={isError}
            isSubmitting={isPending}
            submitError={error}
        />
    );
}
